/**
 * Shared Artifact Types
 *
 * This file contains all artifact-related types shared between frontend and backend.
 * Import artifact types from @event-manager/shared-types, not from local files.
 *
 * Reference: ai-chatbot pattern (examples/ai-chatbot/lib/types.ts, components/artifact.tsx)
 *
 * We only support TEXT and SHEET artifacts (no image, code, etc.)
 */

import type { ArtifactKind } from "./chat";

export type { ArtifactKind } from "./chat";
// Re-export core artifact types from chat.ts
export { ARTIFACT_KINDS, isArtifactKind } from "./chat";

// ============================================================================
// Artifact Status Types
// ============================================================================

/**
 * Artifact status - discriminated union for state machine clarity.
 * Used for both UI rendering and stream handling.
 */
export type ArtifactStatus = "streaming" | "idle" | "loading" | "ready";

/**
 * Const array of artifact statuses for type-safe iteration.
 */
export const ARTIFACT_STATUSES = [
	"streaming",
	"idle",
	"loading",
	"ready",
] as const;

/**
 * Type guard for ArtifactStatus validation.
 */
export function isArtifactStatus(value: unknown): value is ArtifactStatus {
	return (
		typeof value === "string" &&
		ARTIFACT_STATUSES.includes(value as ArtifactStatus)
	);
}

// ============================================================================
// UI Artifact State Types
// ============================================================================

/**
 * Bounding box for artifact positioning.
 * Used for overlay/popup positioning.
 */
export interface ArtifactBoundingBox {
	height: number;
	left: number;
	top: number;
	width: number;
}

/**
 * UI representation of an artifact.
 * This is the runtime state stored in the artifact store.
 *
 * Reference: ai-chatbot UIArtifact interface
 */
export interface UIArtifact {
	boundingBox: ArtifactBoundingBox;
	content: string;
	documentId: string;
	isVisible: boolean;
	kind: ArtifactKind;
	status: ArtifactStatus;
	title: string;
}

/**
 * Initial/default artifact data.
 * Used for initializing artifact state.
 */
export const INITIAL_ARTIFACT_DATA: UIArtifact = {
	documentId: "init",
	content: "",
	kind: "text",
	title: "",
	status: "idle",
	isVisible: false,
	boundingBox: {
		top: 0,
		left: 0,
		width: 0,
		height: 0,
	},
};

// ============================================================================
// Artifact Metadata Types (Per-Kind Type Safety)
// ============================================================================

/**
 * Text artifact metadata.
 * Currently empty, but structured for future extensibility (e.g., suggestions).
 */
export type TextArtifactMetadata = Record<string, never>;

/**
 * Sheet artifact metadata.
 * Currently empty, but structured for future extensibility (e.g., column types).
 */
export type SheetArtifactMetadata = Record<string, never>;

/**
 * Metadata type map - associates each artifact kind with its metadata type.
 * This enables type-safe metadata access based on artifact kind.
 */
export interface ArtifactMetadataMap {
	sheet: SheetArtifactMetadata;
	text: TextArtifactMetadata;
}

/**
 * Get metadata type for a specific artifact kind.
 */
export type MetadataForKind<T extends ArtifactKind> = ArtifactMetadataMap[T];

// ============================================================================
// Artifact Stream Event Types (Backend -> Frontend)
// ============================================================================

/**
 * Text delta stream event.
 * Emitted during text artifact streaming.
 */
export interface TextDeltaEvent {
	readonly data: string;
	readonly transient: true;
	readonly type: "text-delta";
}

/**
 * Sheet delta stream event.
 * Emitted during sheet artifact streaming (CSV format).
 */
export interface SheetDeltaEvent {
	readonly data: string;
	readonly transient: true;
	readonly type: "sheet-delta";
}

/**
 * Finish event with artifact content and kind.
 * Generic over kind K for type-safe completion handling.
 */
export interface ArtifactFinishEvent<K extends ArtifactKind = ArtifactKind> {
	readonly content: string;
	readonly kind: K;
	readonly type: "finish";
}

/**
 * Error event for streaming failures.
 */
export interface ArtifactErrorEvent {
	readonly error: string;
	readonly type: "error";
}

/**
 * Discriminated union of all artifact stream events.
 * Uses readonly modifiers and explicit discriminants for type safety.
 */
export type ArtifactStreamEvent =
	| TextDeltaEvent
	| SheetDeltaEvent
	| ArtifactFinishEvent
	| ArtifactErrorEvent;

/**
 * Type-safe delta event type map.
 * Maps artifact kind to its corresponding delta event type.
 *
 * @example
 * ```typescript
 * // Get the delta event type for a specific kind
 * type TextDelta = DeltaEventMap['text'] // TextDeltaEvent
 * type SheetDelta = DeltaEventMap['sheet'] // SheetDeltaEvent
 * ```
 */
export interface DeltaEventMap {
	sheet: SheetDeltaEvent;
	text: TextDeltaEvent;
}

/**
 * Parameters for artifact streaming.
 * Generic over kind K for type-safe parameter handling.
 *
 * Note: `signal` uses a generic type to avoid DOM dependency in shared-types.
 * At runtime, this will be an AbortSignal in both browser and Node.js environments.
 */
export interface ArtifactStreamParams<K extends ArtifactKind = ArtifactKind> {
	readonly baseContent?: string;
	readonly description?: string;
	readonly kind: K;
	readonly modelId?: string;
	/** Optional abort signal for cancellation */
	readonly signal?: { readonly aborted: boolean };
	readonly title: string;
}
